"use client";

import React from "react";
import Image from "next/image";
import { Title, Description } from "@/components/Common/Typography";
import { ShoppingBag, Tag } from "lucide-react";
import styles from "../export.module.css";

const SackIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 2C11.17 2 10.5 2.67 10.5 3.5C10.5 3.96 10.71 4.36 11.03 4.63C9.28 5.16 8 6.78 8 8.7C8 9.14 8.08 9.57 8.23 9.97L6.2 18.1C5.87 19.42 6.87 20.7 8.23 20.7H15.77C17.13 20.7 18.13 19.42 17.8 18.1L15.77 9.97C15.92 9.57 16 9.14 16 8.7C16 6.78 14.72 5.16 12.97 4.63C13.29 4.36 13.5 3.96 13.5 3.5C13.5 2.67 12.83 2 12 2ZM10 8.7C10 7.6 10.9 6.7 12 6.7C13.1 6.7 14 7.6 14 8.7C14 9.8 13.1 10.7 12 10.7C10.9 10.7 10 9.8 10 8.7Z" />
  </svg>
);

export interface PackagingItem {
  title: string;
  text: string;
  img: string;
}

export interface PackagingSectionProps {
  data?: {
    subtitle?: string;
    title: string;
    description?: string;
    items: PackagingItem[];
  };
}

export default function PackagingSection({ data }: PackagingSectionProps) {
  if (!data || !data.title) return null;

  return (
    <section className={styles.packagingSection}>
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <Title
            as="h2"
            size="medium"
            align="center"
            className={styles.sectionTitle}
          >
            {data.title}
          </Title>
          {data.description && (
            <Description
              as="p"
              size="medium"
              align="center"
              className={styles.sectionDesc}
            >
              {data.description}
            </Description>
          )}
        </div>

        {data.items && data.items.length > 0 && (
          <div className={styles.packagingGrid}>
            {data.items.map((pkg, idx) => {
              const BadgeIcon =
                idx === 0 || idx === 1
                  ? SackIcon
                  : idx === 2
                    ? () => <ShoppingBag size={18} color="#ffffff" />
                    : () => <Tag size={18} color="#ffffff" />;

              return (
                <div key={idx} className={styles.packagingCard}>
                  <div className={styles.packCardImgWrapper}>
                    {pkg.img && (
                      <Image
                        src={pkg.img}
                        alt={pkg.title}
                        fill
                        sizes="(max-width: 768px) 100vw, 25vw"
                        className={styles.packCardImg}
                      />
                    )}
                    <div className={styles.packCardIconBadge}>
                      <BadgeIcon />
                    </div>
                  </div>
                  <div className={styles.packCardBody}>
                    <Title
                      as="h4"
                      size="small"
                      className={styles.packCardTitle}
                    >
                      {pkg.title}
                    </Title>
                    <Description
                      as="p"
                      size="medium"
                      className={styles.packCardText}
                    >
                      {pkg.text}
                    </Description>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
